import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, Sparkles, Loader2, X, ArrowRight, Star, Zap, ScanBarcode, Flame, Camera, LineChart } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { PRICING } from '../mock';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthModal from '../components/AuthModal';
import { toast } from 'sonner';
import { checkout } from '../lib/api';
import { detectCurrency, formatPrice } from '../lib/currency';
import Seo from '../components/Seo';

const LOOKUP = { weekly: 'caloriesnap_weekly', monthly: 'caloriesnap_monthly', yearly: 'caloriesnap_yearly' };

const COMPARE_ROWS = [
  { label: 'Photo calorie recognition', free: '3 per day', pro: 'Unlimited' },
  { label: 'Barcode scanning', free: 'Included in the 3', pro: 'Unlimited' },
  { label: 'Fine-tune ingredients and portions', free: true, pro: true },
  { label: 'Daily calorie & macro tracking', free: true, pro: true },
  { label: 'Personalised daily target', free: true, pro: true },
  { label: 'Streaks & 8pm reminder', free: true, pro: true },
  { label: 'Weekly analytics & trends', free: 'Today only', pro: 'Full history' },
  { label: 'Recipe library', free: false, pro: true },
  { label: 'Priority AI processing', free: false, pro: true },
  { label: 'Priority support', free: false, pro: true },
];

const USE_CASES = [
  { icon: Camera, title: 'Effortless daily tracking', copy: 'Log a full day of meals in under a minute. No search boxes, no barcode-typing, no cheat-day guilt trip.' },
  { icon: Flame, title: 'Weight loss without spreadsheets', copy: 'Get an honest number for every plate and let the ring on your dashboard do the coaching.' },
  { icon: LineChart, title: 'Serious about training', copy: 'Hit macros with confidence — protein target auto-computed from your weight and pace.' },
  { icon: ScanBarcode, title: 'Packaged foods, exact numbers', copy: 'Scan any barcode for exact nutrition from the Open Food Facts database of 3M+ products.' },
];

const MINI_FAQS = [
  { q: 'Do I need a credit card for the free plan?', a: 'No. The Free plan is truly free forever — 3 photo scans per day, no card, no expiry.' },
  { q: 'How does the 1-day trial work?', a: 'When you pick Weekly, Monthly or Yearly, you get 1 day of full Pro access (up to 3 photo scans) before your card is charged. Cancel within that day and pay nothing.' },
  { q: 'What if my country isn\u2019t USD?', a: 'Stripe automatically converts to your local currency at checkout using live exchange rates. We support GBP, EUR, CAD, AUD, JPY and 130+ more.' },
  { q: 'Can I cancel any time?', a: 'Yes, one click from your Dashboard. Your access continues until the end of the current billing period, then you drop to the Free plan.' },
  { q: 'Is my food photo private?', a: 'Every photo is sent over HTTPS and used only for nutrition analysis. We never sell or share your data, and you can delete any meal (and its photo) with one tap.' },
];

const Cell = ({ v }) => {
  if (v === true) return <Check className="w-5 h-5 text-emerald-600" strokeWidth={3} />;
  if (v === false) return <X className="w-5 h-5 text-emerald-950/25" />;
  return <span className="text-emerald-950/80 text-sm">{v}</span>;
};

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [authOpen, setAuthOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const currency = useMemo(() => detectCurrency(), []);

  useEffect(() => { if (params.get('checkout') === 'cancel') toast.info('Checkout cancelled'); }, [params]);

  const choose = async (plan) => {
    if (plan.id === 'free') { if (!user) setAuthOpen(true); else navigate('/app'); return; }
    if (!user) { setAuthOpen(true); return; }
    setLoadingId(plan.id);
    try {
      const { checkout_url } = await checkout(LOOKUP[plan.id]);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#fefdf9]">
      <Seo
        title="Pricing — CalorieSnap plans from free to $5.99/week"
        description="Compare CalorieSnap plans. Free forever with 3 daily scans, or go unlimited from $5.99/week with photo AI, barcode scanning, macro analytics and 1-day free trial. Cancel any time."
        canonical="https://www.thecaloriesnap.com/pricing"
      />
      <Navbar />

      {/* Hero */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto text-center px-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold mb-4"><Sparkles className="w-3.5 h-3.5" />Free forever · 1-day trial on paid plans</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-emerald-950 mb-4">Simple, honest pricing</h1>
          <p className="text-emerald-950/70 text-lg">Start free with 3 photo scans a day. Go unlimited whenever you're hooked, in your local currency. Prices previewed in {currency}; charged at Stripe live rates.</p>
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-4 px-5 mt-14">
          {PRICING.map(p => (
            <div key={p.id} className={`relative rounded-3xl p-7 border transition-all hover:-translate-y-1 flex flex-col ${p.highlighted ? 'bg-emerald-950 text-white border-emerald-950 shadow-2xl' : 'bg-white text-emerald-950 border-emerald-950/10 shadow-sm'}`}>
              {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-400 text-white text-xs font-bold px-3 py-1 rounded-full">{p.badge}</span>}
              <div className="mb-3"><h2 className="font-display text-2xl font-bold">{p.name}</h2><p className={p.highlighted ? 'text-white/70 mt-1 text-sm' : 'text-emerald-950/60 mt-1 text-sm'}>{p.tagline}</p></div>
              <div className="flex items-baseline gap-1 mb-6"><span className="font-display text-4xl font-bold">{p.usd === 0 ? 'Free' : formatPrice(p.usd, currency)}</span>{p.usd !== 0 && <span className={p.highlighted ? 'text-white/70 text-sm' : 'text-emerald-950/60 text-sm'}>{p.period}</span>}</div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {p.features.map(f => (<li key={f} className="flex items-start gap-2.5 text-sm"><span className={`w-5 h-5 rounded-full grid place-items-center shrink-0 mt-0.5 ${p.highlighted ? 'bg-orange-400' : 'bg-emerald-100'}`}><Check className={`w-3 h-3 ${p.highlighted ? 'text-white' : 'text-emerald-700'}`} strokeWidth={3.5} /></span><span className={p.highlighted ? 'text-white/90' : 'text-emerald-950/80'}>{f}</span></li>))}
              </ul>
              <Button disabled={loadingId === p.id} className={`w-full rounded-full h-11 text-sm font-semibold ${p.highlighted ? 'bg-orange-400 hover:bg-orange-500 text-white' : p.usd === 0 ? 'bg-white text-emerald-800 border border-emerald-200 hover:bg-emerald-50' : 'bg-emerald-700 hover:bg-emerald-800 text-white'}`} onClick={() => choose(p)}>{loadingId === p.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{p.cta}</Button>
            </div>
          ))}
        </div>
      </section>

      {/* Value blurbs */}
      <section className="py-16 bg-[#fbf9f2]">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-2xl mb-10">
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-2">Who Pro is for</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-emerald-950">Real progress, without the food-diary drudgery.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {USE_CASES.map((u) => (
              <div key={u.title} className="bg-white rounded-2xl p-6 border border-emerald-950/5">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center mb-3"><u.icon className="w-5 h-5" /></div>
                <h3 className="font-display font-bold text-emerald-950 mb-1.5">{u.title}</h3>
                <p className="text-emerald-950/70 text-sm leading-relaxed">{u.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-5">
          <div className="text-center mb-10">
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-2">Free vs Pro</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-emerald-950">See what's included</h2>
            <p className="text-emerald-950/70 mt-2">Every paid plan (Weekly, Monthly, Yearly) unlocks the same Pro feature set. Pick the billing rhythm that suits you.</p>
          </div>
          <div className="bg-white rounded-3xl border border-emerald-950/5 overflow-hidden">
            <div className="grid grid-cols-3 bg-emerald-50/50 px-6 py-4 text-sm font-semibold text-emerald-950/80">
              <span>Feature</span><span className="text-center">Free</span><span className="text-center">Pro</span>
            </div>
            {COMPARE_ROWS.map((r, i) => (
              <div key={r.label} className={`grid grid-cols-3 px-6 py-3.5 items-center ${i % 2 ? 'bg-emerald-50/20' : ''}`}>
                <span className="text-emerald-950 text-sm">{r.label}</span>
                <span className="flex justify-center"><Cell v={r.free} /></span>
                <span className="flex justify-center"><Cell v={r.pro} /></span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial band */}
      <section className="py-16 bg-emerald-950 text-white">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <div className="flex gap-1 text-amber-300 justify-center mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}</div>
          <p className="font-display text-2xl md:text-3xl leading-relaxed mb-6">"I've tried every calorie counter. This is the only one I've actually stuck with — because logging a meal is literally two taps."</p>
          <p className="text-white/70 text-sm">Amelia R. · Marathon runner · Pro subscriber since 2025</p>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5">
          <div className="text-center mb-10">
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-2">Pricing questions</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-emerald-950">Everything you'd want to know before you pay</h2>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-2">
            {MINI_FAQS.map((f, i) => (
              <AccordionItem key={i} value={`p${i}`} className="bg-white rounded-2xl border border-emerald-950/5 px-5">
                <AccordionTrigger className="font-display font-semibold text-emerald-950 text-left hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-emerald-950/70 leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-5">
          <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-800 text-white p-10 text-center">
            <Zap className="w-8 h-8 mx-auto mb-3 opacity-80" />
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">Start tracking in the next 60 seconds.</h2>
            <p className="text-white/85 mb-6 max-w-lg mx-auto">Sign up with Google, log your first meal, and see your day's calories & macros before your coffee goes cold.</p>
            <Button className="bg-white text-emerald-800 hover:bg-emerald-50 rounded-full h-12 px-8 font-semibold" onClick={() => (user ? navigate('/app') : setAuthOpen(true))}>Start free <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </div>
      </section>

      <Footer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
};

export default Pricing;
